'use client';

import React, { useState, useMemo } from 'react';
import PackageCard from './PackageCard';
import PackageFilters from './PackageFilters';
import PackageSearchBar from './PackageSearchBar';
import PackageCategoryTabs from './PackageCategoryTabs';
import { SlidersHorizontal, X, MapPin } from 'lucide-react';
import { ALL_PACKAGES, CATEGORIES, PICKUP_CITIES, type PickupCity, type TourPackage } from '../data/packagesData';

export type { TourPackage };
export { ALL_PACKAGES };

export interface FilterState {
  durationMin: number;
  durationMax: number;
  budgetMax: number;
  travelTypes: string[];
  seasons: string[];
}

const DEFAULT_FILTERS: FilterState = {
  durationMin: 0,
  durationMax: 15,
  budgetMax: 50000,
  travelTypes: [],
  seasons: [],
};

export default function TourPackagesClient() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<'popular' | 'price-low' | 'price-high' | 'rating'>('popular');
  const [pickupCity, setPickupCity] = useState<PickupCity>('pathankot');

  const filteredPackages = useMemo(() => {
    let result = ALL_PACKAGES;

    if (activeCategory !== 'All') {
      result = result.filter((p) => p.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.destination.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.highlights.some((h) => h.toLowerCase().includes(q))
      );
    }

    result = result.filter(
      (p) =>
        p.durationDays >= filters.durationMin &&
        p.durationDays <= filters.durationMax &&
        p.pickupPricing[pickupCity] <= filters.budgetMax
    );

    if (filters.travelTypes.length > 0) {
      result = result.filter((p) =>
        filters.travelTypes.some((t) => p.travelType.includes(t))
      );
    }

    if (filters.seasons.length > 0) {
      result = result.filter((p) =>
        filters.seasons.some((s) => p.season.includes(s))
      );
    }

    switch (sortBy) {
      case 'price-low':
        return [...result].sort((a, b) => a.pickupPricing[pickupCity] - b.pickupPricing[pickupCity]);
      case 'price-high':
        return [...result].sort((a, b) => b.pickupPricing[pickupCity] - a.pickupPricing[pickupCity]);
      case 'rating':
        return [...result].sort((a, b) => b.rating - a.rating);
      default:
        return [...result].sort((a, b) => b.reviews - a.reviews);
    }
  }, [searchQuery, activeCategory, filters, sortBy, pickupCity]);

  const activeFilterCount =
    filters.travelTypes.length +
    filters.seasons.length +
    (filters.budgetMax < 50000 ? 1 : 0) +
    (filters.durationMax < 15 ? 1 : 0);

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-8">
      {/* Pickup City Selector */}
      <div className="bg-white rounded-2xl border border-border shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2 flex-shrink-0">
            <MapPin size={16} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">Pickup From:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {PICKUP_CITIES.map((city) => (
              <button
                key={city.key}
                onClick={() => setPickupCity(city.key)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all duration-150 ${
                  pickupCity === city.key
                    ? 'border-primary bg-primary text-white shadow-md'
                    : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
                }`}
              >
                {city.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground sm:ml-auto">
            Prices shown for pickup from <span className="font-semibold text-primary">{PICKUP_CITIES.find(c => c.key === pickupCity)?.label}</span>
          </p>
        </div>
      </div>

      {/* Search + Sort Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
        <PackageSearchBar value={searchQuery} onChange={setSearchQuery} />
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setMobileFiltersOpen(true)}
            className="lg:hidden flex items-center gap-2 border border-border bg-white rounded-xl px-4 py-2.5 text-sm font-medium hover:border-primary hover:text-primary transition-colors relative"
          >
            <SlidersHorizontal size={16} />
            Filters
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="input-field w-auto text-sm"
          >
            <option value="popular">Most Popular</option>
            <option value="rating">Highest Rated</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
          </select>
        </div>
      </div>

      {/* Category Tabs */}
      <PackageCategoryTabs
        categories={CATEGORIES}
        active={activeCategory}
        onChange={setActiveCategory}
      />

      {/* Results count */}
      <div className="flex items-center justify-between mb-5 mt-5">
        <p className="text-sm text-muted-foreground">
          <span className="font-bold text-foreground">{filteredPackages.length}</span>{' '}
          packages found
          {activeCategory !== 'All' && (
            <span>
              {' '}in <span className="text-primary font-semibold">{activeCategory}</span>
            </span>
          )}
        </p>
        {(activeFilterCount > 0 || activeCategory !== 'All' || searchQuery) && (
          <button
            onClick={() => {
              setFilters(DEFAULT_FILTERS);
              setActiveCategory('All');
              setSearchQuery('');
            }}
            className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-600 font-medium transition-colors"
          >
            <X size={14} />
            Clear all filters
          </button>
        )}
      </div>

      <div className="flex gap-6">
        {/* Sidebar Filters — Desktop */}
        <div className="hidden lg:block w-64 xl:w-72 flex-shrink-0">
          <PackageFilters
            filters={filters}
            onChange={setFilters}
            onReset={() => setFilters(DEFAULT_FILTERS)}
          />
        </div>

        {/* Package Grid */}
        <div className="flex-1 min-w-0">
          {filteredPackages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mb-4">
                <SlidersHorizontal size={28} className="text-muted-foreground" />
              </div>
              <h3 className="font-bold text-foreground text-lg mb-2">No packages match your filters</h3>
              <p className="text-muted-foreground text-sm max-w-sm mb-5">
                Try adjusting your filters, changing the category, or searching with different keywords.
              </p>
              <button
                onClick={() => {
                  setFilters(DEFAULT_FILTERS);
                  setActiveCategory('All');
                  setSearchQuery('');
                }}
                className="btn-primary"
              >
                Reset All Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
              {filteredPackages.map((pkg) => (
                <PackageCard key={pkg.id} pkg={pkg} pickupCity={pickupCity} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Filters Drawer */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white overflow-y-auto fade-in">
            <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-white z-10">
              <h3 className="font-bold text-foreground">Filters</h3>
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
                aria-label="Close filters"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4">
              <PackageFilters
                filters={filters}
                onChange={setFilters}
                onReset={() => {
                  setFilters(DEFAULT_FILTERS);
                  setMobileFiltersOpen(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
